// ABOUTME: One feeling's full 2A scene: its looping sky-and-sea art, the floating face and the feeling's name above it.
// ABOUTME: Drawn on the prototype's 850 x 370 stage, scaled to fit and anchored so the stage's horizon lands on the sea's horizon line.

import { Box, Typography } from '@mui/material';
import { CROSSING_FACE_FILL, CROSSING_NEUTRAL_FILL, CROSSING_HEADLINE_INK, FEELING_HUES } from '../../../../theme/crossingTokens';
import type { CrossingFeelingDto } from '../../../../types/api';
import FeelingFaceIcon from '../../crossing/FeelingFaceIcon';
import { useHorizon } from '../../crossing/useHorizon';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import { useFrameScale } from '../../crossing/useFrameScale';
import { WR_RIDE_ANIMATION, WR_RIDE_KEYFRAMES } from '../../crossing/wrRide';
import { FEELING_FIGURE, FEELING_KEYFRAMES, FEELING_SCENES, FEELING_SPRITE_PX, ScaredDim } from './feelingScenes';

// The prototype's stage: 470 plate plus the 190px of sea it shows each side, 100px shorter than the plate. The horizon is
// at 60% of its height and the face sits on it. The scene paints behind the screen's own text and arrows (z -1 inside the
// screen layer, which is its own stacking context).
const STAGE_WIDTH = 850;
const STAGE_HEIGHT = 370;
const STAGE_HORIZON = 0.6 * STAGE_HEIGHT;
const PLATE_WIDTH = 470;
const FACE_SIZE = 66;
const FACE_ICON_SIZE = 54;
// The ring's centre stays where the bare 54px face's centre was (165.2 + 27).
const FACE_TOP = 165.2 - (FACE_SIZE - FACE_ICON_SIZE) / 2;
const NAME_HEIGHT = 44;

interface FeelingSceneProps {
  feeling: CrossingFeelingDto;
}

export default function FeelingScene({ feeling }: FeelingSceneProps) {
  const reducedMotion = useCrossingReducedMotion();
  const { frameSc } = useFrameScale(PLATE_WIDTH);
  const { line } = useHorizon();
  const Body = Object.prototype.hasOwnProperty.call(FEELING_SCENES, feeling.code) ? FEELING_SCENES[feeling.code] : undefined;
  const figure = Object.prototype.hasOwnProperty.call(FEELING_FIGURE, feeling.code) ? FEELING_FIGURE[feeling.code] : undefined;
  const ride = reducedMotion ? { animation: 'none' } : { animation: WR_RIDE_ANIMATION };

  return (
    <>
      {feeling.code === 'scared' && (
        <Box aria-hidden="true" sx={{ position: 'fixed', left: 0, right: 0, top: 0, height: line, pointerEvents: 'none', zIndex: -1, ...FEELING_KEYFRAMES }}>
          <ScaredDim />
        </Box>
      )}
      <Box
        data-testid={`feeling-scene-${feeling.code}`}
        sx={{
          position: 'fixed', left: '50%', top: `${line - STAGE_HORIZON * frameSc}px`, width: STAGE_WIDTH, height: STAGE_HEIGHT,
          marginLeft: `${-STAGE_WIDTH / 2}px`, transform: `scale(${frameSc})`, transformOrigin: '50% 0', pointerEvents: 'none', zIndex: -1,
          ...FEELING_KEYFRAMES, ...WR_RIDE_KEYFRAMES,
        }}
      >
        {Body && figure && (
          <Box
            data-testid="feeling-art"
            sx={{ position: 'absolute', inset: 0, transform: `scale(${FEELING_SPRITE_PX / figure.width})`, transformOrigin: `${figure.cx}px ${figure.cy}px` }}
          >
            <Body />
          </Box>
        )}
        <Typography
          data-testid="feeling-scene-name"
          sx={{
            position: 'absolute', left: '50%', top: FACE_TOP - NAME_HEIGHT, width: 300, marginLeft: '-150px', textAlign: 'center',
            fontFamily: 'Newsreader, serif', fontSize: '44px', fontWeight: 600, lineHeight: `${NAME_HEIGHT}px`, color: CROSSING_HEADLINE_INK, ...ride,
          }}
        >
          {feeling.name}
        </Typography>
        <Box
          role="img"
          aria-label={feeling.sentence}
          sx={{
            position: 'absolute', left: '50%', top: FACE_TOP, width: FACE_SIZE, height: FACE_SIZE, marginLeft: `${-FACE_SIZE / 2}px`,
            boxSizing: 'border-box', borderRadius: '50%', border: `3px solid ${FEELING_HUES[feeling.code] ?? CROSSING_NEUTRAL_FILL}`,
            backgroundColor: CROSSING_FACE_FILL, display: 'flex', alignItems: 'center', justifyContent: 'center', ...ride,
          }}
        >
          <FeelingFaceIcon code={feeling.code} hue={FEELING_HUES[feeling.code] ?? CROSSING_NEUTRAL_FILL} size={FACE_ICON_SIZE} />
        </Box>
      </Box>
    </>
  );
}
